import {
  useDeleteProductOrderRequestMutation,
  useGetProductOrderRequestDetailsQuery,
  useLazyGetNewOrdersCountQuery,
  useUpdateProductOrderRequestStatusMutation,
} from '@api/productRequest.api';
import { ProductRequestStatusType } from '@api/types';
import { TextCodes } from '@api/types/translation/TextCodes';
import {
  BackButton,
  BlockContainer,
  LoadingError,
  ProductRequestDelete,
  ProductRequestDetailsSkeleton,
  ProductRequestProductBox,
  ProductRequestStatus,
  SmartLink,
  Title,
} from '@components';
import { useAppSelector, useAuth } from '@hooks';
import { Box, Divider, Grid, Stack, Typography, styled } from '@mui/material';
import { getSpecialPage } from '@store/app/app.slice';
import { colors } from '@themes/colors';
import { t } from '@translations';

import dayjs from 'dayjs';
import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

const MessageBubble = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1, 2),
  borderRadius: 4,
  marginBottom: theme.spacing(1),
  whiteSpace: 'pre-wrap',
  backgroundColor: theme.palette.background.paper,
  marginTop: theme.spacing(1),
}));

type ProductOrderRequestDetailsBlockProps = {
  title?: string;
};

export const ProductOrderRequestDetailsBlock: React.FC<ProductOrderRequestDetailsBlockProps> = ({
  title,
}: ProductOrderRequestDetailsBlockProps) => {
  const location = useLocation();
  const { pathname } = location;
  const currentPage = useAppSelector(getSpecialPage('productOrderRequestDetailsPage')) as string;
  const navigate = useNavigate();
  const { isBeremaUser } = useAuth();
  const urlParams = new URLSearchParams(location.search);
  const id = urlParams.get('id');
  const [updateStatusTrigger] = useUpdateProductOrderRequestStatusMutation();
  const [deleteTrigger] = useDeleteProductOrderRequestMutation();
  const { currentData, isLoading, isError, isSuccess } = useGetProductOrderRequestDetailsQuery(
    id || '',
    {
      skip: !id,
    }
  );
  const isLoadingError = (isError || !id) && currentPage?.includes(pathname);
  const requestDate = dayjs(currentData?.timestamp, { utc: true }).format('DD.MM.YYYY HH:mm');
  const productOrderRequestListPage = useAppSelector(getSpecialPage('productOrderRequestListPage'));

  const handleDropdownChange = (value: ProductRequestStatusType) => {
    if (currentData?.id) {
      updateStatusTrigger({ entityId: currentData?.id, status: value });
    }
  };

  const handleDelete = () => {
    if (currentData?.id) {
      deleteTrigger(currentData?.id).then(() => {
        if (productOrderRequestListPage) {
          navigate(productOrderRequestListPage);
        }
      });
    }
  };

  const [trigger] = useLazyGetNewOrdersCountQuery({
    refetchOnFocus: true,
    pollingInterval: 60 * 1000 * 5,
    refetchOnReconnect: true,
  });

  useEffect(() => {
    if (currentData && isSuccess && !isBeremaUser) {
      trigger();
    }
  }, [trigger, currentData, isSuccess, isBeremaUser]);

  if (isLoading) return <ProductRequestDetailsSkeleton />;

  return (
    <BlockContainer>
      <BackButton href={productOrderRequestListPage} />
      {title ? (
        <Title variant='h2' sx={{ mb: 4 }}>
          {title}
        </Title>
      ) : null}
      {isLoadingError ? (
        <LoadingError />
      ) : (
        <>
          <Box display='flex' justifyContent='space-between'>
            {/* Dealer Name */}
            <Title variant='h3' noDot>
              {currentData?.dealer?.name || ''}
            </Title>
            <Stack direction='row' spacing={2} alignItems='center'>
              {/* Product Request Info Status */}
              <ProductRequestStatus
                handleDropdownChange={handleDropdownChange}
                currentStatus={currentData?.status}
                canChangeStatus={true}
              />
              {isBeremaUser && <ProductRequestDelete handleDelete={handleDelete} />}
            </Stack>
          </Box>
          <Divider sx={{ mb: 2, mt: 1 }} />
          <Title variant='h3' noDot>
            {t(TextCodes.ProductRequest.Base.RequestDetails)}
          </Title>
          <Grid
            container
            spacing={{
              xs: 2,
              md: 4,
            }}
          >
            <Grid item xs={12} md={6}>
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                mb={2}
                mt={4}
                spacing={{
                  xs: 2,
                  md: 4,
                }}
              >
                <div>
                  <Typography color={colors.text.secondary} fontWeight='bold !important'>
                    {t(TextCodes.ProductRequest.Base.RequestDate)}
                  </Typography>
                  <Typography variant='body2'>{requestDate}</Typography>
                </div>
                <div>
                  <Typography color={colors.text.secondary} fontWeight='bold !important'>
                    {t(TextCodes.ProductRequest.Base.DealerName)}
                  </Typography>
                  <Typography variant='body2'>{currentData?.dealer?.name}</Typography>
                </div>
                {currentData?.contact?.email && (
                  <div>
                    <Typography color={colors.text.secondary} fontWeight='bold !important'>
                      {t(TextCodes.ProductRequest.Order.Email)}
                    </Typography>
                    <SmartLink
                      href={'mailto:' + currentData?.contact?.email}
                      sx={{
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        maxWidth: {
                          xs: '100%',
                          md: '150px',
                          lg: '200px',
                        },
                      }}
                    >
                      {currentData?.contact?.email}
                    </SmartLink>
                  </div>
                )}
                {currentData?.contact?.phoneNumber ? (
                  <div>
                    <Typography color={colors.text.secondary} fontWeight='bold !important'>
                      {t(TextCodes.ProductRequest.Order.PhoneNumber)}
                    </Typography>
                    <Typography variant='body2'>{currentData?.contact?.phoneNumber}</Typography>
                  </div>
                ) : null}
              </Stack>
              <ProductRequestProductBox product={currentData?.product} />
              <Typography color={colors.text.secondary} fontWeight='bold !important'>
                {t(TextCodes.ProductRequest.Order.DeliveryNote)}
              </Typography>
              <MessageBubble>{currentData?.deliveryNote}</MessageBubble>
            </Grid>
          </Grid>
        </>
      )}
    </BlockContainer>
  );
};
